// ============================================================================
// OUTCOMES: what happened to the quotes that actually went out.
// ----------------------------------------------------------------------------
// This is the honest tiny-n panel. QuoteMaker has not been reliably capturing
// won and lost, so the figures here are real but few. The design answer is
// readiness 'thin': show the REAL counts AND the activation note naming what
// would switch the panel on. Never a fabricated rate, never a dark panel that
// hides the two answers that do exist.
//
// No win-rate percentage is computed anywhere in this file. A percentage over a
// handful of answers reads as authoritative and is theatre: 1 win out of 2
// answers is not "50% win rate", it is two answers. Rates by trade, client and
// value band are DORMANT per SPEC section 3.10 until the decided population
// grows, and stubbing them now would teach the operator to trust noise.
//
// Population: quotes that have been sent or decided, i.e. outcome in
// {sent, won, lost, expired}. A draft that was never sent has no outcome to
// report and is deliberately outside the denominator.
// ============================================================================

import { money } from '../model';
import type { DerivedRow, QuoteOutcome } from '../model';
import type {
    AnalysisContext,
    Cell,
    DrillTarget,
    LaneKey,
    PanelDef,
    PanelReadiness,
    PanelSeries,
} from '../registry';

/**
 * Below this many recorded answers the panel refuses to read as 'live'.
 * Five is the point at which the counts stop being anecdotes; it is NOT the
 * threshold for a win rate, which SPEC section 3.10 puts at ten decided.
 */
const DECISION_FLOOR = 5;

/**
 * The census phrase is written out here rather than imported from the registry
 * on purpose. The registry imports every panel, so a value import back from a
 * panel would close a runtime cycle, and a test that imports THIS module
 * directly would then evaluate the registry mid-flight and hit the TDZ on its
 * own default export. Type-only imports are erased, so this file has no runtime
 * edge to the registry at all.
 */
function census(n: number, capped: boolean, noun: string): string {
    return `n=${n}${capped ? ' (capped)' : ''} ${noun}`;
}

const SENT_OR_DECIDED: ReadonlySet<QuoteOutcome> = new Set<QuoteOutcome>([
    'sent', 'won', 'lost', 'expired',
]);

interface FigureSpec {
    key: string;
    label: string;
    outcome: QuoteOutcome;
    /** Where the Run Sheet holds these rows. */
    lane: LaneKey;
    /**
     * True only when the destination lane contains exactly these rows and
     * nothing else. `laneOf` maps outcome 'sent' onto 'waiting' one to one, so
     * the awaiting cell lands clean; won, lost and expired all share the
     * 'decided' lane, so each of those is a subset and must say so.
     */
    exact: boolean;
    note?: string;
}

const SUBSET_NOTE = 'The run sheet cannot filter by outcome. This opens the decided lane, newest first.';

const FIGURES: readonly FigureSpec[] = [
    { key: 'won', label: 'Won', outcome: 'won', lane: 'decided', exact: false, note: SUBSET_NOTE },
    { key: 'lost', label: 'Lost', outcome: 'lost', lane: 'decided', exact: false, note: SUBSET_NOTE },
    { key: 'expired', label: 'Expired', outcome: 'expired', lane: 'decided', exact: false, note: SUBSET_NOTE },
    { key: 'awaiting', label: 'Awaiting an answer', outcome: 'sent', lane: 'waiting', exact: true },
];

function sumValue(rows: DerivedRow[]): number {
    return rows.reduce((s, r) => s + r.value, 0);
}

/** Answers actually on the record: everything except the still-awaiting cell. */
function recordedAnswers(s: PanelSeries): number {
    return s.cells.reduce((n, c) => (c.key === 'awaiting' ? n : n + c.count), 0);
}

function buildDrill(spec: FigureSpec): DrillTarget {
    const base = { lane: spec.lane, query: '', sort: 'newest' as const, exact: spec.exact };
    return spec.note ? { ...base, note: spec.note } : base;
}

function compute(ctx: AnalysisContext): PanelSeries {
    // respectsPeriod is false: "what happened to everything we ever sent" is the
    // question, and slicing it by creation date answers a different, weaker one.
    const population = ctx.allRows.filter(r => SENT_OR_DECIDED.has(r.outcome));
    const total = { count: population.length, value: sumValue(population) };

    if (population.length === 0) {
        return {
            cells: [],
            total,
            emptySeries: 'Nothing has been sent or decided yet.',
        };
    }

    const groups = FIGURES.map(spec => ({
        spec,
        rows: population.filter(r => r.outcome === spec.outcome),
    }));
    // Weight rides the COUNT, not the value: this panel counts answers, and a
    // single large won job must not make "won" look like most of the book.
    const maxCount = groups.reduce((m, g) => Math.max(m, g.rows.length), 0);

    const cells: Cell[] = groups.map(({ spec, rows: group }) => ({
        key: spec.key,
        label: spec.label,
        count: group.length,
        value: sumValue(group),
        weight: maxCount > 0 ? group.length / maxCount : 0,
        // No `tone`. Lane tones are identity-only and the colour budget gives
        // this panel exactly one coloured mark, the won figure, which the shell
        // keys off the cell key. A tone here would leak lane colour onto four
        // cards that are not a lane strip.
        select: (rows: DerivedRow[]) => rows.filter(r => r.outcome === spec.outcome),
        formula: (picked: DerivedRow[]) => {
            const n = picked.length;
            return `${n} ${n === 1 ? 'row' : 'rows'} where outcome = ${spec.outcome}`
                + ` · sum(scope[].amount) ex GST = ${money(sumValue(picked))}`;
        },
        drill: buildDrill(spec),
    }));

    return { cells, total };
}

function readiness(s: PanelSeries, ctx?: AnalysisContext): PanelReadiness {
    // An empty window is a shell empty state, not dormancy: the source is fine,
    // the filter simply matched nothing. Rows present but none sent or decided IS
    // honest dormancy, and keeps its dashed border and activation note.
    if (ctx && ctx.rows.length === 0) return 'live';
    if (s.total.count === 0) return 'dormant';
    // Real figures, honestly labelled as too few to generalise from. Never
    // 'live' on a handful: that is the state that would imply a win rate.
    return recordedAnswers(s) < DECISION_FLOOR ? 'thin' : 'live';
}

function activation(s: PanelSeries, ctx: AnalysisContext): string | null {
    const state = readiness(s, ctx);
    if (state === 'live') return null;

    if (state === 'dormant') {
        return 'No quote has been sent or decided yet, so there is nothing to report here.'
            + ' Outcomes accrue as quotes are marked won or lost, and the run sheet\'s decided'
            + ' lane is where that gets recorded.';
    }

    const answers = recordedAnswers(s);
    const book = ctx.allRows.length;
    return `Only ${answers} of ${book} ${book === 1 ? 'quote' : 'quotes'}`
        + ` ${answers === 1 ? 'carries' : 'carry'} a recorded answer, so a win rate would be noise.`
        + ' Mark quotes won or lost as answers come in and this fills in with real rates by trade,'
        + ' client and value band. The run sheet\'s decided lane is where that gets recorded.';
}

function stamp(s: PanelSeries, ctx: AnalysisContext): string {
    const answers = recordedAnswers(s);
    return `${census(s.total.count, ctx.window.capped, 'sent or decided')}`
        + ` · quote_status + aiContext.wonAt/lostAt`
        + ` · ${answers} ${answers === 1 ? 'answer' : 'answers'} recorded`;
}

function esc(v: string): string {
    return v
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function chartLabel(s: PanelSeries): string {
    if (s.cells.length === 0) return 'Outcomes: nothing sent or decided yet';
    const top = [...s.cells]
        .sort((a, b) => b.count - a.count)
        .slice(0, 3)
        .map(c => `${c.count} ${c.label.toLowerCase()}`)
        .join(', ');
    return `Outcomes: ${top}`;
}

/**
 * Static SVG for the printed report. The print document is served with
 * script-src 'none', so every coordinate below is resolved here in JS and
 * serialised as a literal attribute: nothing measures, nothing adjusts after
 * layout. Colour is currentColor plus the two variables the spec names, each
 * with a fallback to currentColor, so the tile degrades to ink rather than to a
 * wrong brand colour and no hex literal is authored.
 */
function printSvg(s: PanelSeries, w: number, h: number): string {
    const width = Math.max(1, Math.round(w));
    const height = Math.max(1, Math.round(h));
    const open = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}"`
        + ` viewBox="0 0 ${width} ${height}" role="img" aria-label="${esc(chartLabel(s))}">`;

    if (s.cells.length === 0) {
        const line = s.emptySeries || 'Nothing has been sent or decided yet.';
        return `${open}<text x="0" y="${Math.round(height / 2)}" font-size="11"`
            + ` fill="currentColor" fill-opacity="0.72">${esc(line)}</text></svg>`;
    }

    const gap = 10;
    const n = s.cells.length;
    const cardW = Math.max(1, (width - gap * (n - 1)) / n);
    const cardH = Math.max(1, Math.min(height, 84));
    const yNum = Math.round(cardH * 0.42);
    const yLabel = Math.min(cardH - 16, yNum + 16);
    const yValue = Math.min(cardH - 4, yLabel + 14);

    const cards = s.cells.map((c, i) => {
        const x = Math.round(i * (cardW + gap) * 100) / 100;
        const cw = Math.round(cardW * 100) / 100;
        const stroke = c.key === 'won'
            ? 'var(--qm-status-success-medium, currentColor)'
            : 'currentColor';
        const strokeOpacity = c.key === 'won' ? '1' : '0.28';
        const ink = c.key === 'won'
            ? 'var(--qm-status-success-dark, currentColor)'
            : 'currentColor';
        return `<g transform="translate(${x},0)">`
            + `<rect x="0.75" y="0.75" width="${Math.max(0.5, cw - 1.5)}" height="${cardH - 1.5}"`
            + ` rx="8" fill="currentColor" fill-opacity="0.03"`
            + ` stroke="${stroke}" stroke-opacity="${strokeOpacity}" stroke-width="1.5" />`
            + `<text x="10" y="${yNum}" font-size="20" font-weight="800" fill="${ink}"`
            + ` style="font-variant-numeric:tabular-nums">${c.count}</text>`
            + `<text x="10" y="${yLabel}" font-size="10" font-weight="650" fill="${ink}"`
            + ` fill-opacity="0.85">${esc(c.label)}</text>`
            + `<text x="10" y="${yValue}" font-size="9" fill="currentColor" fill-opacity="0.62"`
            + ` style="font-variant-numeric:tabular-nums">${esc(money(c.value))} ex GST</text>`
            + `</g>`;
    }).join('');

    return `${open}${cards}</svg>`;
}

const outcomesPanel: PanelDef = {
    id: 'outcomes',
    title: 'Outcomes',
    hint: 'what happened to the quotes that went out',
    group: 'outcome',
    defaultSpan: 6,
    minSpan: 6,
    slim: false,
    mark: 'figures',
    respectsPeriod: false,

    compute,
    readiness,
    activation,
    stamp,
    // The stamp counts sent-or-decided rows only. See PanelDef.population.
    population: (rows: DerivedRow[]) => rows.filter(r => SENT_OR_DECIDED.has(r.outcome)),

    fields: [
        'quoteStatus',
        'status',
        'aiContext.wonAt',
        'aiContext.lostAt',
        'scope[].amount',
        'scopeGstInclusive',
    ],
    caveats: [
        'A quote marked won in Stage 5 and later marked lost in All Quotes reads as lost: the canonical quote_status wins.',
        'Nothing expires on its own. Expired is a recorded answer someone entered, not a quote that quietly lapsed.',
    ],

    csv(s: PanelSeries) {
        return {
            headers: ['Outcome', 'Jobs', 'Value ex GST'],
            rows: s.cells.map(c => ({
                Outcome: c.label,
                Jobs: c.count,
                'Value ex GST': Math.round(c.value),
            })),
        };
    },

    printSvg,
};

export default outcomesPanel;
